import * as Sentry from '@sentry/react';
import { useMachine } from '@xstate/react';
import { assign, send, spawn } from 'xstate';

import { useAuthCoreMachine } from '@nw/auth-core';
import {
  Config,
  CUSTOM_ID,
  LogEntriesConstantsEnums,
  LogEntryPriorityEnum,
  LogEntryTypesEnums,
  PAYMENT_SUBMIT_CLICKED_ANALYTICS,
  USER_VERIFIED_OTP
} from '@nw/shared/config';
import {
  clearAllClientDomainTokens,
  customEvents,
  useBackendSegmentApi,
  useCreateFreeOrder,
  useGetClientAuthCodeAPI,
  useGetInitialProductConfig,
  useGetOrderStatus,
  useGetPaymentsClientAuthDetails,
  useGetReferralCode,
  useGetUserLogoutAPI,
  useTrackReferralAttemptApi,
  useGetUserProfileAPI,
  useStorageUtils,
  useUpdateUserExtraInfoDetails,
  useUpdateUserProfileAPI
} from '@nw/shared/data-access';
import {
  captureLogEntriesExceptionWithScope,
  EMAIL_NOT_REGISTERED,
  getAcceptedTandC,
  getAPIErrorEnum,
  getAPIErrorMessage,
  getCreateOrderRequestObject,
  getHiddenFormFieldsValues,
  getLanguageEnum,
  getQueryParamValue,
  getSegmentPropertiesObject,
  getSelectedSlotLanguageEnumBasedOnPreferredWebinarSlot,
  getServerDateFormat,
  getTimeZone,
  getUpdateProfileRequestObject,
  getURLSearchParam,
  getWebinarDateTimeFromSelectedSlot,
  INVITE_CODE,
  loadSegmentScript,
  paymentInitiationAnalytics,
  paymentSuccessAnalytics,
  PHONE_NUMBER_NOT_REGISTERED,
  PHONE_NUMBER_NOT_VERIFIED,
  segment,
  sendConfigGAEventToDataLayer,
  sendLoginFlowEventToDataLayer,
  sendSegmentIdentifyAnalytics,
  sendSegmentUpdateProfileIdentifyEvent,
  USER_ALREADY_REGISTERED
} from '@nw/shared/utils';

import { getUserProfileDetailsTransformer, UserProfileModel } from '../../../';
import { UserProfileDetailsDTO } from '../../../shared/stores/dtos/userProfileDetail.dtos';
import { SegmentEvent } from '../../../shared/stores/models/SegmentEvent';
import {
  getFormattedRequestObject,
  getFormattedRequestObjectKeys
} from '../../../shared/utils/FieldUtils';
import { getValuesWithNormalizedDistrictForUpdateProfile } from '../../../shared/utils/RequestObjectUtils';

import {
  productFormMachineActions,
  productFormsMachineEvents
} from '../../constants/formMachineConstants';
import {
  ACADEMY_AI_WORKSHOP,
  ACADEMY_MCP_WORKSHOP,
  CCBP_ACADEMY_IITIANS_WEBINAR_SOCIALMEDIA,
  productsWithAuthenticationAndSubmitForm
} from '../../constants/ProductConstants';
import { getProductFromPricingType } from '../../factories/ProductPricingTypeFactory';
import { publicFormsMachine } from '../../machines/PublicFormsMachine';
import {
  AssignConfigEventToContext,
  ProductFormsMachineContext,
  ProductFormsMachineEvent,
  PublicProductFormMachineSendType,
  PublicProductFormMachineStateType,
  SendBackendSegmentEvent,
  SendFrontendSegmentEvent,
  SendGAEvent
} from '../../machines/PublicFormsMachine/types';
import { DefaultProductConfigDTO } from '../../stores/dtos/product.dtos';
import DefaultProduct from '../../stores/models/ProductModels/DefaultProduct';
import { getFormConfigTransformer } from '../../stores/transformers/formConfigTransformer';
import { DefaultProductTypes } from '../../types/ProductTypes';
import { getCycleData } from '../../utils/WebinarRegistrationUtils';

import { getEventDetailsFromEventName } from './publicFormMachineUtils';

interface FormMachineProps {
  productCode: string;
  localProductConfig?: DefaultProductTypes;
  shouldLoggedOutUser?: boolean;
}

export type UsePublicMachineReturnType = {
  status: {
    getInitialConfigStatus: string;
    getReferralCodesStatus: string;
    getProfileDetailsStatus: string;
  };
  errors: {
    getInitialConfigError: string;
    getReferralCodesError: string;
    getProfileDetailsError: string;
  };
  publicFormsMachineState: any;
  publicFormsMachineSend: any;
};

export const usePublicFormsMachine = (
  props: FormMachineProps
): UsePublicMachineReturnType => {
  const { getAuthCoreMachineWithConfig } = useAuthCoreMachine();

  const params = getURLSearchParam();

  const {
    productCode,
    localProductConfig,
    shouldLoggedOutUser = false
  } = props;
  const {
    getAccessToken,
    getPaymentAccessToken,
    setPaymentsAccessTokenToDomain,
    setPaymentsRefreshTokenToDomain,
    setUserId,
    setAccessTokenToDomain,
    setRefreshTokenToDomain,
    clearUserSession
  } = useStorageUtils();

  const { userLogoutAPI } = useGetUserLogoutAPI();

  const { getClientAuthCodeAPI } = useGetClientAuthCodeAPI();
  const { getPaymentsClientAuthDetailsAPI } = useGetPaymentsClientAuthDetails();

  const {
    getUserProfileDataApi,
    status: getProfileDetailsStatus,
    error: getProfileDetailsError
  } = useGetUserProfileAPI();

  const { updateUserProfileDetailsAPI } = useUpdateUserProfileAPI();

  const { createFreeOrderApi } = useCreateFreeOrder();
  const { getOrderStatusApi } = useGetOrderStatus();
  const {
    getReferralCodeApi,
    status: getReferralCodeStatus,
    error: getReferralCodeError,
    isInvalidLicenceCodeCarryForwardConfigNotFoundError
  } = useGetReferralCode();
  const { updateUserExtraInfoApi } = useUpdateUserExtraInfoDetails();
  const { sendBackendSegmentApi } = useBackendSegmentApi();
  const { trackReferralAttemptApi } = useTrackReferralAttemptApi();

  const {
    getInitialConfigAPI,
    status: getInitialConfigStatus,
    error: getInitialConfigError
  } = useGetInitialProductConfig();

  const actions = productFormMachineActions;
  const events = productFormsMachineEvents;

  const isUserAlreadyLoggedIn = () => {
    if (productsWithAuthenticationAndSubmitForm.includes(productCode)) {
      return false;
    } else return Boolean(getAccessToken()) && Boolean(getPaymentAccessToken());
  };

  const checkShouldLoggedOutUser = () => {
    if (productsWithAuthenticationAndSubmitForm.includes(productCode)) {
      return true;
    } else return shouldLoggedOutUser;
  };

  //TODO: Give types for context and event  and refactor segment API
  const formsMachineWithConfig = publicFormsMachine(
    isUserAlreadyLoggedIn(),
    checkShouldLoggedOutUser()
  ).withConfig({
    actions: {
      logoutFromSegment: () => {
        segment.reset();
      },

      [actions.spawnAuthCoreMachine]: assign((context: any, event) => {
        return {
          authMachineRef: spawn(
            getAuthCoreMachineWithConfig(context.values['phone']),
            { sync: true }
          )
        };
      }),

      assignProductConfigLocally: assign(
        (context: ProductFormsMachineContext, event: any) => {
          if (localProductConfig) {
            const productConfigData: DefaultProductConfigDTO =
              getFormConfigTransformer(localProductConfig);

            const defaultProductInfo: DefaultProduct =
              getProductFromPricingType(
                productConfigData,
                context.userProfileDetails
              );

            if (defaultProductInfo.extraData?.should_format_request_object) {
              return {
                productInfo: defaultProductInfo,
                formattedRequestObjectKeys: getFormattedRequestObjectKeys(
                  defaultProductInfo.requestObjectExtraFieldData
                )
              };
            } else
              return {
                productInfo: defaultProductInfo
              };
          }
          return {};
        }
      ),
      assignErrorMessageToContext: assign((context: any, event: any) => {
        return {
          errorMessage: getAPIErrorMessage(event.data.message)
        };
      }),
      setNextAndSubmitBtnLoadingToTrue: assign(
        (
          context: ProductFormsMachineContext,
          event: ProductFormsMachineEvent
        ) => {
          return {
            isNextAndSubmitBtnLoading: true
          };
        }
      ),
      setNextAndSubmitBtnLoadingToFalse: assign(
        (
          context: ProductFormsMachineContext,
          event: ProductFormsMachineEvent
        ) => {
          return {
            isNextAndSubmitBtnLoading: false
          };
        }
      ),
      [actions.setIsOtpButtonLoadingToFalse]: assign(
        (
          context: ProductFormsMachineContext,
          event: ProductFormsMachineEvent
        ) => {
          return {
            isOTPButtonLoading: false
          };
        }
      ),
      [actions.setIsOtpButtonLoadingToTrue]: assign(
        (
          context: ProductFormsMachineContext,
          event: ProductFormsMachineEvent
        ) => {
          return {
            isOTPButtonLoading: true
          };
        }
      ),
      [actions.doChildMachineSelfTransition]: send(
        { type: events.doSelfTransition },
        {
          to: (context: any) => context.authMachineRef
        }
      ),
      sendPreRegistrationEventToDataLayer: (
        context: ProductFormsMachineContext
      ) => {
        const { productInfo } = context;
        if (productInfo)
          paymentInitiationAnalytics(
            PAYMENT_SUBMIT_CLICKED_ANALYTICS,
            productInfo.program,
            productInfo.productCode
          );
      },
      sendSuccessEventToDataLayer: (context: ProductFormsMachineContext) => {
        const { productInfo, orderDetails } = context;
        if (productInfo) {
          paymentSuccessAnalytics(
            productInfo.program,
            orderDetails.order_id,
            productInfo.productCode
          );
        }
      },
      validateDataAndAssignProductConfigToContext: assign(
        (context: ProductFormsMachineContext, event: any) => {
          const productConfigData: DefaultProductConfigDTO =
            getFormConfigTransformer(event.data);

          const productInfo: DefaultProduct = getProductFromPricingType(
            productConfigData,
            context.userProfileDetails
          );

          if (productInfo.extraData?.should_format_request_object) {
            return {
              productInfo,
              formattedRequestObjectKeys: getFormattedRequestObjectKeys(
                productInfo.requestObjectExtraFieldData
              )
            };
          } else
            return {
              productInfo
            };
        }
      ),
      validateAndSetUserProfileDetailsToContext: assign(
        (context: ProductFormsMachineContext, event: any) => {
          Sentry.setUser({ id: event.data.user_id });
          const userProfileDetailsData: UserProfileDetailsDTO =
            getUserProfileDetailsTransformer(event.data);

          const userProfileDetails: UserProfileModel = new UserProfileModel(
            userProfileDetailsData
          );

          return {
            userProfileDetails: userProfileDetails
          };
        }
      ),
      loadSegmentScript: () => {
        loadSegmentScript();
      },
      sendIdentifySegmentEvent: (context) => {
        sendSegmentIdentifyAnalytics(
          context.userProfileDetails as UserProfileModel,
          context.values
        );
      },
      saveUserIdToCookie: (context, event: any) => {
        setUserId(event.data.user_id);
      },
      sendOTPVerifiedOTPAnalytics: (context) => {
        const { isOtpVerified } = context;
        if (isOtpVerified) {
          segment.track(USER_VERIFIED_OTP, {});
        }
      },
      sendUserVerifiedOTPEventToSegment: (context) => {
        const { productInfo, userProfileDetails, inviteCode, values } = context;
        if (productInfo) {
          const { segmentEventsTypes, shouldSendSegmentEvent } =
            productInfo.productConfig;
          const {
            fullName,
            email,
            phoneNumber,
            currentState,
            selectedOccupation,
            selectedGender,
            whatsappInfoStatus,
            amount,
            currentCity,
            currentDistrict,
            pincode,
            ...others
          } = values;

          const extraInfo = {
            current_city: currentCity,
            current_district: currentDistrict,
            pincode: pincode,
            program: productInfo.program,
            ...others
          };

          const hiddenFormFieldsValues = getHiddenFormFieldsValues(
            productInfo.form.allFormFields,
            values
          );

          const productCode = productInfo.productCode;
          const userDetails = {
            full_name: fullName || userProfileDetails?.name,
            email: email || userProfileDetails?.email,
            phone_number: phoneNumber || userProfileDetails?.phoneNumber,
            state_of_residence: currentState,
            occupation: selectedOccupation,
            gender: selectedGender,
            i_want_to_receive_updates_directly_on_whatsapp: whatsappInfoStatus
              ? whatsappInfoStatus.toString().toUpperCase()
              : 'FALSE'
          };
          const extraInfoBasedOnProductConfig = JSON.stringify({
            ...extraInfo,
            ...hiddenFormFieldsValues
          });
          const acceptedTnc = getAcceptedTandC(productInfo);
          const verifySuccessTrackEvent =
            segmentEventsTypes?.verificationSuccessTrackEvent;

          if (shouldSendSegmentEvent && verifySuccessTrackEvent) {
            const trackEventProperties = getSegmentPropertiesObject(
              verifySuccessTrackEvent as SegmentEvent,
              userProfileDetails as UserProfileModel,
              {
                ...getCreateOrderRequestObject(
                  inviteCode,
                  productCode,
                  userDetails,
                  extraInfoBasedOnProductConfig,
                  0,
                  acceptedTnc
                ),
                invite_code: inviteCode
              },
              params
            );
            segment.track(
              verifySuccessTrackEvent.eventName,
              trackEventProperties,
              verifySuccessTrackEvent.segmentEventIntegrationConfig
            );
          }
        }
      },
      assignReferralCodesToContext: assign((context: any, event: any) => {
        return {
          referralCodes: event.data
        };
      }),
      updateInviteCodeInContext: assign((context: any, event: any) => {
        const referralCode = event.data.referral_code;
        const status = event.data.status;
        const inviteCode =
          status === 'ACTIVE'
            ? referralCode || context.inviteCode
            : context.inviteCode || referralCode;

        return {
          inviteCode: inviteCode
        };
      }),
      updateIsInviteCodePresentInBackend: assign((context: any, event: any) => {
        return {
          isInviteCodePresentInBackend: Boolean(event.data.referral_code)
        };
      }),
      [actions.onErrorTrackReferralAttemptLogEntry]: (
        context: ProductFormsMachineContext,
        event: any
      ) => {
        const res_status = getAPIErrorEnum(event?.data);
        const attemptedLicenceCode = getQueryParamValue(INVITE_CODE);
        captureLogEntriesExceptionWithScope(
          LogEntriesConstantsEnums.TRACK_REFERRAL_ATTEMPT_API_FAILED,
          {
            res_status,
            requestObject: {
              product_code: productCode,
              existing_licence_code: context.referralCodes?.referral_code,
              attempted_licence_code: attemptedLicenceCode ?? ''
            },
            currentPageUrl:
              typeof window !== 'undefined' ? window.location.href : '',
            error: event?.data
          },
          LogEntryTypesEnums.INFORMATION,
          LogEntryPriorityEnum.MEDIUM
        );
      },
      assignSelfTransitionToConfigEvents: assign((context: any, event: any) => {
        return {
          eventsFromConfig: [
            events.doSelfTransition,
            ...context.eventsFromConfig
          ]
        };
      }),
      [actions.assignChildSelfTransitionToConfigEvents]: assign(
        (context: any, event: any) => {
          return {
            eventsFromConfig: [
              events.doChildMachineSelfTransition,
              ...context.eventsFromConfig
            ]
          };
        }
      ),
      setGoToNextSteps: assign((context: any, event: any) => {
        if (event.shouldRemove) {
          const goToNextSteps = context.goToNextSteps.slice(1);
          return {
            goToNextSteps: goToNextSteps
          };
        } else {
          return {
            goToNextSteps: [...context.goToNextSteps, 'GO_TO_NEXT_STEP']
          };
        }
      }),
      callEventsFromConfig: (
        context: ProductFormsMachineContext,
        event: ProductFormsMachineEvent
      ) => {
        const { eventsFromConfig } = context;
        if (eventsFromConfig.length > 0) {
          const nextConfigEvents = eventsFromConfig.slice(1);
          const eventDetails = getEventDetailsFromEventName(
            eventsFromConfig[0],
            context.values
          );
          publicFormsMachineSend({
            type: events.assignConfigEventsToContext,
            configEvent: nextConfigEvents
          } as AssignConfigEventToContext);
          publicFormsMachineSend(eventDetails);
        }
      },
      assignConfigEventsToContext: assign(
        (context: any, event: ProductFormsMachineEvent) => {
          event = event as AssignConfigEventToContext;
          context.eventsFromConfig = event.configEvent;
          if (event.values) {
            return { values: { ...context.values, ...event.values } };
          } else {
            return {};
          }
        }
      ),
      clearErrorMessageFromContext: assign((context: any, event: any) => {
        return {
          errorMessage: ''
        };
      }),
      resetOTP: assign((context: any, event: any) => {
        return {
          values: {
            ...context.values,
            otp: ''
          }
        };
      }),
      callNextFromContext: (context: any, event: any) => {
        context.next && context.next();
      },
      callBackFromContext: (context, event) => {
        context.back && context.back();
      },
      disableGetOTPButton: assign((context, event) => {
        return {
          shouldShowGetOTP: false
        };
      }),
      enableGetOTPButton: assign((context, event) => {
        return {
          shouldShowGetOTP: true
        };
      }),
      updateIsOtpSentToUserInContext: assign((context, event) => {
        return {
          isOtpSentToUser: true
        };
      }),
      updateFormValuesToContext: assign((context, event: any) => {
        return {
          values: { ...context.values, ...event.values }
        };
      }),
      updateFormSubmittedValues: assign((context, event: any) => {
        return {
          formSubmittedValues: { ...context.values }
        };
      }),
      updateProfileRequestObject: assign((context, event: any) => {
        const { values } = context;
        return {
          updateProfileRequestObject: getUpdateProfileRequestObject(
            {},
            getValuesWithNormalizedDistrictForUpdateProfile(values)
          )
        };
      }),
      onSuccessGettingClientAuthCode: (context: any, event: any) => {
        context.authCode = event.data.code;
      },
      sendIdentifySegmentEventOnOrderSuccess: (context, event) => {
        sendSegmentUpdateProfileIdentifyEvent(
          context.userProfileDetails as UserProfileModel,
          context.values
        );
      },
      onSuccessGettingClientAuthenticationDetails: (_context, event: any) => {
        setPaymentsAccessTokenToDomain(event.data.access_token);
        setPaymentsRefreshTokenToDomain(event.data.refresh_token);
      },
      updateOrderDetailsInContext: assign((context: any, event: any) => {
        return {
          orderDetails: event.data
        };
      }),
      sendGetOtpCustomEventToDataLayer: (context, event) => {
        sendLoginFlowEventToDataLayer(customEvents.getOtp, productCode, {
          product_code: productCode
        });
      },
      [actions.sendResendOtpEventToDataLayer]: (_context, _event) => {
        sendLoginFlowEventToDataLayer(customEvents.resendOtp, productCode, {
          product_code: productCode
        });
      },
      updateIsNewUserInContextToTrue: assign((context: any, event) => {
        return {
          isNewUser: true
        };
      }),
      updateIsNewUserInContextToFalse: assign((context: any, event) => {
        return {
          isNewUser: false
        };
      }),
      sendUserVerifiedOtpEvent: (context, event) => {
        if (context.isNewUser) {
          sendLoginFlowEventToDataLayer(
            customEvents.otpVerifySignUp,
            productCode,
            {
              product_code: productCode
            }
          );
        } else {
          sendLoginFlowEventToDataLayer(
            customEvents.otpVerifyLogin,
            productCode,
            {
              product_code: productCode
            }
          );
        }
      },

      updateIsLoggedInInContext: assign((context, event: any) => {
        return {
          isLoggedIn: true
        };
      }),
      clearPrefilledFormDataAction: assign(
        (context: ProductFormsMachineContext) => {
          clearUserSession();

          return {
            ...context,
            userProfileDetails: null,
            values: {},
            isLoggedIn: false,
            isOtpVerified: false,
            isOtpSentToUser: false,
            shouldShowGetOTP: false,
            authCode: '',
            authMachineRef: null,
            referralCodes: null
          };
        }
      ),
      setIsOtpVerifiedInContext: assign((context, event: any) => {
        return {
          isOtpVerified: context.isOtpSentToUser ? true : false
        };
      }),
      captureSegmentApiFailureSentryException: (context, event: any) => {
        Sentry.captureException(event.data);
      },
      [actions.sendGAEventToDataLayer]: (
        context: ProductFormsMachineContext,
        event
      ) => {
        event = event as SendGAEvent;
        const { productInfo, orderDetails, values } = context;
        const googleAnalyticsEvents =
          productInfo?.productConfig.googleAnalyticsEvents;

        if (googleAnalyticsEvents) {
          sendConfigGAEventToDataLayer(googleAnalyticsEvents, event.eventName, {
            ...values,
            ...orderDetails
          });
        }
      },
      [actions.sendFrontendSegmentEvent]: (context, event) => {
        const { productInfo, userProfileDetails, inviteCode, values } = context;
        if (!productInfo) return;

        event = event as SendFrontendSegmentEvent;
        const { segmentEventsTypes, shouldSendSegmentEvent } =
          productInfo.productConfig;
        const {
          fullName,
          email,
          phoneNumber,
          currentState,
          selectedOccupation,
          selectedGender,
          whatsappInfoStatus,
          currentCity,
          currentDistrict,
          pincode,
          ...others
        } = values;

        const extraInfo = {
          current_city: currentCity,
          current_district: currentDistrict,
          pincode: pincode,
          program: productInfo.program,
          ...others
        };

        const hiddenFormFieldsValues = getHiddenFormFieldsValues(
          productInfo.form.allFormFields,
          values
        );

        const productCode = productInfo.productCode;
        const userDetails = {
          full_name: fullName || userProfileDetails?.name,
          email: email || userProfileDetails?.email,
          phone_number: phoneNumber || userProfileDetails?.phoneNumber,
          state_of_residence: currentState,
          occupation: selectedOccupation,
          gender: selectedGender,
          i_want_to_receive_updates_directly_on_whatsapp: whatsappInfoStatus
            ? whatsappInfoStatus.toString().toUpperCase()
            : 'FALSE'
        };
        const extraInfoBasedOnProductConfig = JSON.stringify({
          ...extraInfo,
          ...hiddenFormFieldsValues
        });
        const acceptedTnc = getAcceptedTandC(productInfo);
        const eventName = event.eventName;

        const segmentEventDetail =
          segmentEventsTypes && segmentEventsTypes[eventName];

        if (shouldSendSegmentEvent && segmentEventDetail) {
          const segmentEventProperties = getSegmentPropertiesObject(
            segmentEventDetail,
            userProfileDetails as UserProfileModel,
            {
              ...getCreateOrderRequestObject(
                inviteCode,
                productCode,
                userDetails,
                extraInfoBasedOnProductConfig,
                0,
                acceptedTnc
              ),
              invite_code: inviteCode
            },
            params
          );
          segment.track(
            segmentEventDetail.eventName,
            segmentEventProperties,
            segmentEventDetail.segmentEventIntegrationConfig
          );
        }
      },
      [actions.setLoginAccessAndRefreshToken]: (context, event: any) => {
        clearAllClientDomainTokens();
        setAccessTokenToDomain(event.data.accessToken);
        setRefreshTokenToDomain(event.data.refreshToken);
      },
      sendAlreadyRegisteredProductSegmentEvent: (context, event: any) => {
        const { productInfo, userProfileDetails, inviteCode, values } = context;
        if (!productInfo) return;

        const { segmentEventsTypes, shouldSendSegmentEvent } =
          productInfo.productConfig;

        const {
          fullName,
          email,
          phoneNumber,
          currentState,
          selectedOccupation,
          selectedGender,
          whatsappInfoStatus,
          currentCity,
          currentDistrict,
          pincode,
          ...others
        } = values;

        const extraInfo = {
          current_city: currentCity,
          current_district: currentDistrict,
          pincode: pincode,
          program: productInfo.program,
          ...others
        };

        const hiddenFormFieldsValues = getHiddenFormFieldsValues(
          productInfo.form.allFormFields,
          values
        );

        const productCode = productInfo.productCode;
        const userDetails = {
          full_name: fullName || userProfileDetails?.name,
          email: email || userProfileDetails?.email,
          phone_number: phoneNumber || userProfileDetails?.phoneNumber,
          state_of_residence: currentState,
          occupation: selectedOccupation,
          gender: selectedGender,
          i_want_to_receive_updates_directly_on_whatsapp: whatsappInfoStatus
            ? whatsappInfoStatus.toString().toUpperCase()
            : 'FALSE'
        };
        const extraInfoBasedOnProductConfig = JSON.stringify({
          ...extraInfo,
          ...hiddenFormFieldsValues
        });
        const acceptedTnc = getAcceptedTandC(productInfo);

        const segmentEventDetail =
          segmentEventsTypes &&
          segmentEventsTypes['already_registered_track_event'];

        if (shouldSendSegmentEvent && segmentEventDetail) {
          const segmentEventProperties = getSegmentPropertiesObject(
            segmentEventDetail,
            userProfileDetails as UserProfileModel,
            {
              ...getCreateOrderRequestObject(
                inviteCode,
                productCode,
                userDetails,
                extraInfoBasedOnProductConfig,
                0,
                acceptedTnc
              ),
              invite_code: inviteCode
            },
            params
          );
          segment.track(
            segmentEventDetail.eventName,
            segmentEventProperties,
            segmentEventDetail.segmentEventIntegrationConfig
          );
        }
      }
    },
    services: {
      logoutUser: (context, event: any) => {
        const isUserLoggedIn = getAccessToken();
        if (isUserLoggedIn && shouldLoggedOutUser) {
          context.isOtpVerified = false;
          context.isLoggedIn = false;
          return userLogoutAPI();
        } else {
          return Promise.resolve();
        }
      },
      getInitialConfig: (context: any, event: any) => {
        return getInitialConfigAPI({ productCode });
      },
      getClientAuthCode: () =>
        getClientAuthCodeAPI({ client_id: Config['OTG_PAYMENTS_CLIENT_ID'] }),
      getClientAuthenticationDetails: (context: any, event: any) => {
        const requestObject = {
          client_id: Config['OTG_PAYMENTS_CLIENT_ID'],
          code: context.authCode
        };
        return getPaymentsClientAuthDetailsAPI(requestObject);
      },
      getReferralCodes: (context: any) => {
        return getReferralCodeApi({
          productCode
        });
      },
      trackReferralAttempt: (context: any) => {
        const inviteCodeFromParam = getQueryParamValue(INVITE_CODE);
        if (!inviteCodeFromParam) return Promise.resolve({});
        return trackReferralAttemptApi({
          product_code: productCode,
          existing_licence_code: context.referralCodes?.referral_code,
          attempted_licence_code: inviteCodeFromParam
        });
      },
      getProfileDetails: () => getUserProfileDataApi(),

      updateProfile: (context: any, event: any) => {
        const { userProfileDetails, values } = context;
        return updateUserProfileDetailsAPI(
          getUpdateProfileRequestObject(
            userProfileDetails,
            getValuesWithNormalizedDistrictForUpdateProfile(values)
          )
        );
      },

      createFreeOrder: (context, event) => {
        const {
          values,
          userProfileDetails,
          productInfo,
          inviteCode,
          formattedRequestObjectKeys
        } = context;

        const {
          fullName,
          email,
          phone,
          currentState,
          selectedOccupation,
          selectedGender,
          whatsappInfoStatus,
          amount,
          currentCity,
          currentDistrict,
          pincode,
          inviteCode: inviteCodeValue,
          countryCode,
          dialCode,
          program,
          otp,
          current_state,
          current_district,
          ...others
        } = values;
        const extraInfo = {
          current_state: currentState,
          current_city: currentCity,
          current_district: currentDistrict?.district_name || current_district,
          pincode: pincode,
          program,
          ...others
        };
        const hiddenFormFieldsValues = getHiddenFormFieldsValues(
          productInfo?.form.allFormFields,
          values
        );

        const calculatedInviteCode = inviteCodeValue
          ? inviteCodeValue
          : inviteCode;

        let segmentDateTime: string | undefined = undefined;
        const preferredWebinarSlot =
          values['languagePreferenceWebinarSlot'] ||
          values['preferredWebinarSlot'];
        const webinar1DateTime =
          values['utmBasedWebinarDate'] || values['webinar1DateTime'];
        if (webinar1DateTime && preferredWebinarSlot) {
          segmentDateTime = getWebinarDateTimeFromSelectedSlot(
            webinar1DateTime,
            preferredWebinarSlot
          );
        }

        const requestObject = {
          invite_code: calculatedInviteCode,
          product_code: productInfo?.productCode as string,
          user_details: {
            full_name: fullName || userProfileDetails?.name,
            email: email || userProfileDetails?.email,
            phone_number: phone || userProfileDetails?.phoneNumber,
            state_of_residence: currentState,
            occupation: selectedOccupation,
            gender: selectedGender,
            i_want_to_receive_updates_directly_on_whatsapp: whatsappInfoStatus
              ? whatsappInfoStatus.toString().toUpperCase()
              : 'FALSE'
          },
          amount_to_be_paid_in_least_subunit: 0,
          accepted_tnc: getAcceptedTandC(productInfo)
        };

        const customId = params.get(CUSTOM_ID);

        let webinarRegistrationRequestObject: any = {};
        if (
          productInfo?.productCode ===
          CCBP_ACADEMY_IITIANS_WEBINAR_SOCIALMEDIA ||
          productInfo?.productCode === ACADEMY_AI_WORKSHOP ||
          productInfo?.productCode === ACADEMY_MCP_WORKSHOP
        ) {
          const preferredLanguageEnum =
            values['preferredLanguage'] &&
            getLanguageEnum(values['preferredLanguage']);

          webinarRegistrationRequestObject = {
            ...webinarRegistrationRequestObject,
            preferredDateSlot: webinar1DateTime,
            webinar1DateTime: webinar1DateTime,
            preferredTimeSlot: preferredWebinarSlot,
            preferredWebinarSlot: preferredWebinarSlot,
            preferred_language: preferredLanguageEnum,
            userTimeZone: getTimeZone(),
            webinarCycle: getCycleData(''),
            selected_slot_language:
              getSelectedSlotLanguageEnumBasedOnPreferredWebinarSlot(
                preferredWebinarSlot
              ) ?? preferredLanguageEnum
          };
        }

        const extraInfoBasedOnConfig = {
          ...extraInfo,
          ...hiddenFormFieldsValues,
          custom_id: customId ?? undefined,
          segmentDateTime: segmentDateTime,
          selected_webinar_slot_datetime:
            segmentDateTime && getServerDateFormat(new Date(segmentDateTime)),
          ...webinarRegistrationRequestObject
        };

        if (formattedRequestObjectKeys) {
          (requestObject as any)['extra_info_based_on_product_config'] =
            JSON.stringify({
              ...getFormattedRequestObject(
                extraInfoBasedOnConfig,
                formattedRequestObjectKeys
              ),
              custom_id: customId ?? undefined
            });
        } else {
          (requestObject as any)['extra_info_based_on_product_config'] =
            JSON.stringify(extraInfoBasedOnConfig);
        }

        return createFreeOrderApi(requestObject);
      },

      getOrderStatus: (context, event) => {
        return getOrderStatusApi({ orderId: context.orderDetails.order_id });
      },
      updateUserExtraInfo: (context, event: any) => {
        const { formattedRequestObjectKeys, values } = context;

        const valuesToUpdate = Object.keys(values).reduce(
          (uniqueValues: Record<string, any>, key) => {
            if (!(key in context.formSubmittedValues)) {
              uniqueValues[key] = values[key];
            }
            return uniqueValues;
          },
          {}
        );

        let { otp, ...rest } = valuesToUpdate;

        if (formattedRequestObjectKeys) {
          rest = getFormattedRequestObject(rest, formattedRequestObjectKeys);
        }

        return updateUserExtraInfoApi({
          values: { ...rest },
          orderId: context.orderDetails.order_id
        });
      },
      callBackendSegmentApi: (context, event) => {
        const {
          productInfo,
          userProfileDetails,
          inviteCode,
          values,
          orderDetails,
          updateProfileRequestObject
        } = context;

        if (!productInfo) return Promise.resolve();

        event = event as SendBackendSegmentEvent;

        const { segmentEventsTypes, shouldSendSegmentEvent } =
          productInfo.productConfig;

        const {
          fullName,
          email,
          phoneNumber,
          currentState,
          selectedOccupation,
          selectedGender,
          whatsappInfoStatus,
          amount,
          currentCity,
          currentDistrict,
          pincode,
          ...others
        } = values;

        let webinarRegistrationRequestObject: any = {};

        if (
          productInfo?.productCode === CCBP_ACADEMY_IITIANS_WEBINAR_SOCIALMEDIA
        ) {
          const isEmailGiven = updateProfileRequestObject?.['email']
            ? true
            : false;
          webinarRegistrationRequestObject = {
            ...webinarRegistrationRequestObject,
            isEmailGiven: isEmailGiven
          };
        }

        const extraInfo = {
          current_city: currentCity,
          current_district: currentDistrict,
          pincode: pincode,
          program: productInfo.program,
          ...webinarRegistrationRequestObject,
          ...others
        };

        const hiddenFormFieldsValues = getHiddenFormFieldsValues(
          productInfo.form.allFormFields,
          values
        );

        const productCode = productInfo.productCode;
        const userDetails = {
          full_name: fullName || userProfileDetails?.name,
          email: email || userProfileDetails?.email,
          phone_number: phoneNumber || userProfileDetails?.phoneNumber,
          state_of_residence: currentState,
          occupation: selectedOccupation,
          gender: selectedGender,
          i_want_to_receive_updates_directly_on_whatsapp: whatsappInfoStatus
            ? whatsappInfoStatus.toString().toUpperCase()
            : 'FALSE'
        };
        const extraInfoBasedOnProductConfig = JSON.stringify({
          ...extraInfo,
          ...hiddenFormFieldsValues
        });
        const amountToBePaidInLeastSubUnit = amount
          ? typeof amount === 'number'
            ? amount * 100
            : Number(amount.split(',').join('')) * 100
          : 0;
        const acceptedTnc = getAcceptedTandC(productInfo);

        const eventName = event.eventName;
        const segmentEventDetail =
          segmentEventsTypes && segmentEventsTypes[eventName];

        return shouldSendSegmentEvent && segmentEventDetail
          ? sendBackendSegmentApi({
            event: segmentEventDetail.eventName,
            eventData: JSON.stringify(
              getSegmentPropertiesObject(
                segmentEventDetail,
                userProfileDetails as UserProfileModel,
                {
                  ...getCreateOrderRequestObject(
                    inviteCode,
                    productCode,
                    userDetails,
                    extraInfoBasedOnProductConfig,
                    amountToBePaidInLeastSubUnit,
                    acceptedTnc
                  ),
                  invite_code: inviteCode
                },
                params
              )
            ),
            orderId: orderDetails.order_id
          })
          : Promise.resolve();
      }
    },
    guards: {
      shouldLoadScriptLocally: (context: any, event: any) => {
        return localProductConfig ? true : false;
      },
      isNewUserError: (context: any, event: any) => {
        return (
          getAPIErrorEnum(event.data) === PHONE_NUMBER_NOT_REGISTERED ||
          getAPIErrorEnum(event.data) === EMAIL_NOT_REGISTERED
        );
      },
      isPhoneNumberNotVerifiedError: (context: any, event: any) => {
        return getAPIErrorEnum(event.data) === PHONE_NUMBER_NOT_VERIFIED;
      },
      isProductInfoPresent: (context, event: any) => {
        return context.productInfo ? true : false;
      },
      isLoggedIn: (context, event: any) => {
        return context.isLoggedIn ? true : false;
      },
      isNotLoggedIn: (context, event: any) => {
        return context.isLoggedIn ? false : true;
      },
      isAlreadyRegisteredProductError: (context, event: any) => {
        return Boolean(getAPIErrorEnum(event.data) === USER_ALREADY_REGISTERED);
      },
      isInvalidProductCodeErrorAndOtpVerified: (context, event: any) => {
        return (
          getAPIErrorEnum(event.data) ===
          'LICENCE_CODE_CARRY_FORWARD_CONFIG_IS_NOT_FOUND_FOR_PRODUCT_CODE' &&
          context.isOtpVerified === true
        );
      },
      isInvalidProductCodeErrorAndNotOtpVerified: (context, event: any) => {
        return (
          getAPIErrorEnum(event.data) ===
          'LICENCE_CODE_CARRY_FORWARD_CONFIG_IS_NOT_FOUND_FOR_PRODUCT_CODE' &&
          context.isOtpVerified !== true
        );
      },
      isOtpNotVerified: (context, event: any) => {
        return context.isOtpVerified !== true;
      },
      shouldTrackSecondaryReferralAttempt: (context: any, event: any) => {
        const referralCodeFromBackend = event?.data?.referral_code;
        const inviteCodeFromQuery = context.inviteCode;
        return Boolean(
          referralCodeFromBackend &&
          inviteCodeFromQuery &&
          referralCodeFromBackend !== inviteCodeFromQuery
        );
      }
    }
  });

  const [publicFormsMachineState, publicFormsMachineSend] = useMachine(
    formsMachineWithConfig
  );

  const shouldOverrideReferralCodeStatus =
    getReferralCodeStatus === 'error' && isInvalidLicenceCodeCarryForwardConfigNotFoundError;

  return {
    publicFormsMachineState,
    publicFormsMachineSend,
    status: {
      getInitialConfigStatus,
      getReferralCodesStatus: shouldOverrideReferralCodeStatus
        ? 'success'
        : getReferralCodeStatus,
      getProfileDetailsStatus
    },
    errors: {
      getInitialConfigError,
      getReferralCodesError: shouldOverrideReferralCodeStatus
        ? ''
        : getReferralCodeError,
      getProfileDetailsError
    }
  };
};
